import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { Ban, CircleCheck, CircleX, ExternalLink, History, Hourglass, Loader2, MessageSquare, RotateCcw } from "lucide-react";
import { assistantApi, isUpgradeError } from "./assistantApi";
import type { ActionStatus, AssistantAction } from "./types";

const STATUS_META: Record<ActionStatus, { label: string; cls: string; Icon: React.ElementType }> = {
  executed: { label: "Done", cls: "bg-emerald-50 text-emerald-700", Icon: CircleCheck },
  pending: { label: "Waiting", cls: "bg-amber-50 text-amber-700", Icon: Hourglass },
  skipped: { label: "Skipped", cls: "bg-slate-100 text-slate-500", Icon: Ban },
  failed: { label: "Failed", cls: "bg-red-50 text-red-600", Icon: CircleX },
  expired: { label: "Expired", cls: "bg-slate-100 text-slate-500", Icon: Hourglass },
};

interface Props {
  canSeeEveryone: boolean;
  onUpgradeRequired: () => void;
  onOpenThread: (threadId: string) => void;
}

const ActivityTab: React.FC<Props> = ({ canSeeEveryone, onUpgradeRequired, onOpenThread }) => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<AssistantAction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [everyone, setEveryone] = useState(false);
  const [everyoneAllowed, setEveryoneAllowed] = useState(canSeeEveryone);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await assistantApi.listActions({ all: everyone, limit: 50 });
      setRows(data);
    } catch (err: any) {
      if (isUpgradeError(err)) {
        onUpgradeRequired();
      } else if (everyone && err?.response?.status === 403) {
        setEveryoneAllowed(false);
        setEveryone(false);
      } else {
        setError("Could not load activity. Try again in a moment.");
      }
    } finally {
      setLoading(false);
    }
  }, [everyone, onUpgradeRequired]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between gap-2 border-b border-slate-100 px-4 py-2.5">
        <p className="text-xs font-semibold text-slate-500">
          {everyone ? "Everyone at the firm" : "Your actions"}
        </p>
        {everyoneAllowed && (
          <div role="group" aria-label="Whose activity" className="flex rounded-lg bg-slate-100 p-0.5 text-xs font-semibold">
            {[
              { v: false, label: "Mine" },
              { v: true, label: "Everyone" },
            ].map((o) => (
              <button
                key={o.label}
                type="button"
                aria-pressed={everyone === o.v}
                onClick={() => setEveryone(o.v)}
                className={`min-h-[32px] rounded-md px-2.5 transition-colors ${
                  everyone === o.v ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex h-40 items-center justify-center text-slate-400">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center px-6 py-12 text-center">
            <p className="text-sm text-slate-600">{error}</p>
            <button
              type="button"
              onClick={load}
              className="mt-3 inline-flex min-h-[40px] items-center gap-1.5 rounded-xl border border-slate-200 px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Retry
            </button>
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center px-6 py-14 text-center">
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-slate-100">
              <History className="h-6 w-6 text-slate-400" />
            </div>
            <p className="text-sm font-semibold text-slate-700">No actions yet</p>
            <p className="mt-1 text-xs text-slate-500">
              Texts, tasks, time entries and anything else the assistant does will be listed here.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {rows.map((a) => {
              const meta = STATUS_META[a.status] || STATUS_META.pending;
              const link = a.result?.link || a.preview?.link;
              const when = a.executedAt || a.createdAt;
              let rel = "";
              try {
                rel = when ? formatDistanceToNow(new Date(when), { addSuffix: true }) : "";
              } catch {}
              return (
                <li key={a._id} className="flex items-start gap-3 px-4 py-3">
                  <span className={`mt-0.5 inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${meta.cls}`}>
                    <meta.Icon className="h-3 w-3" />
                    {a.status === "executed" && a.mode === "auto" ? "Auto" : meta.label}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-slate-800">
                      {a.preview?.title || a.tool}
                    </p>
                    <p className="text-xs text-slate-500">
                      {[everyone && a.userName, rel].filter(Boolean).join(" \u00b7 ")}
                    </p>
                  </div>
                  {a.status === "pending" && a.threadId && !everyone ? (
                    <button
                      type="button"
                      onClick={() => onOpenThread(a.threadId)}
                      aria-label={`Open the chat for ${a.preview?.title || "this action"}`}
                      className="inline-flex min-h-[44px] shrink-0 items-center gap-1 rounded-lg px-2 text-xs font-semibold text-[#0F5132] hover:bg-emerald-50 sm:min-h-[32px]"
                    >
                      <MessageSquare className="h-3.5 w-3.5" />
                      Open chat
                    </button>
                  ) : link?.href && (
                    <button
                      type="button"
                      onClick={() => navigate(link.href)}
                      aria-label={`Open ${a.preview?.title || "record"}`}
                      className="inline-flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-50 hover:text-[#0F5132] sm:min-h-[32px] sm:min-w-[32px]"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default ActivityTab;
